import React, { useState, useEffect } from 'react';
import { Card, Button, Skeleton, TextArea } from '../components/ui';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Unsubscribe,
  CheckCircle,
  Error as ErrorIcon,
  Campaign,
  Notifications,
  Article,
  BlockOutlined,
  Info
} from '../mui-icons';
import './UnsubscribePage.css';
import logoIcon from '../assets/icon-no-name.webp';

const API_BASE = 'https://api.personalnutri.app';

/**
 * Decodifica o token base64 URL-safe para obter o email
 */
function decodeUnsubscribeToken(token) {
  try {
    // base64url para base64 padrão
    const base64 = token.replace(/-/g, '+').replace(/_/g, '/');
    // Adicionar padding se necessário
    const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);
    // Decodificar
    const email = atob(padded);
    return email;
  } catch (error) {
    console.error('Erro ao decodificar token:', error);
    return null;
  }
}

/**
 * Valida formato de email
 */
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Mascara parcialmente o email para exibição
 */
function maskEmail(email) {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  
  const maskedLocal = local.length > 3 
    ? local.substring(0, 3) + '***' 
    : local.substring(0, 1) + '***';
  
  return `${maskedLocal}@${domain}`;
}

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t');
  
  // States
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Opções de cancelamento
  const [options, setOptions] = useState({
    marketing: true,
    notification: true,
    newsletter: true,
    global: false,
    reason: ''
  });
  
  // Validar token ao carregar
  useEffect(() => {
    if (!token) {
      setError('Link inválido. Token não encontrado na URL.');
      setLoading(false);
      return;
    }
    
    const decodedEmail = decodeUnsubscribeToken(token);
    
    if (!decodedEmail || !isValidEmail(decodedEmail)) {
      setError('Link inválido ou expirado. Não foi possível identificar o email.');
      setLoading(false);
      return;
    }
    
    setEmail(decodedEmail);
    setLoading(false);
  }, [token]);
  
  // Ao marcar "cancelar todos", marca os outros
  const handleGlobalChange = (checked) => {
    setOptions(prev => ({
      ...prev,
      global: checked,
      marketing: checked || prev.marketing,
      notification: checked || prev.notification,
      newsletter: checked || prev.newsletter
    }));
  };
  
  // Handler para checkboxes individuais
  const handleOptionChange = (field, checked) => {
    setOptions(prev => {
      const newOptions = { ...prev, [field]: checked };
      // Se desmarcou algum, desmarcar global
      if (!checked && prev.global) {
        newOptions.global = false;
      }
      return newOptions;
    });
  };
  
  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Verificar se pelo menos uma opção está selecionada
    if (!options.marketing && !options.notification && !options.newsletter && !options.global) {
      setError('Selecione pelo menos uma categoria de email para cancelar.');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE}/unsubscribe?t=${token}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          marketing: options.marketing,
          notification: options.notification,
          newsletter: options.newsletter,
          global: options.global,
          reason: options.reason.trim()
        })
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setSuccess(true);
      } else {
        setError(data.message || 'Erro ao processar cancelamento. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao chamar API:', err);
      setError('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Loading
  if (loading) {
    return (
      <div className="unsubscribe-page surface-app">
        <div className="unsubscribe-container">
          <Card variant="auth">
            <div className="unsubscribe-logo">
              <img src={logoIcon} alt="PersonalNutri" />
            </div>
            <Skeleton width="60%" height={24} borderRadius={4} style={{ margin: '16px auto' }} />
            <Skeleton width="100%" height={120} borderRadius={12} />
          </Card>
        </div>
      </div>
    );
  }
  
  // Erro (token inválido)
  if (error && !email) {
    return (
      <div className="unsubscribe-page surface-app">
        <div className="unsubscribe-container">
          <Card variant="auth" className="unsubscribe-card">
            <div className="unsubscribe-logo">
              <img src={logoIcon} alt="PersonalNutri" />
            </div>
            <div className="error-state">
              <div className="status-icon error">
                <ErrorIcon sx={{ fontSize: 64 }} />
              </div>
              <h2>Link Inválido</h2>
              <p>{error}</p>
              <Link to="/" className="btn-back">
                Voltar para PersonalNutri
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }
  
  // Sucesso
  if (success) {
    return (
      <div className="unsubscribe-page surface-app">
        <div className="unsubscribe-container">
          <Card variant="auth" className="unsubscribe-card">
            <div className="unsubscribe-logo">
              <img src={logoIcon} alt="PersonalNutri" />
            </div>
            <div className="success-state">
              <div className="status-icon success">
                <CheckCircle sx={{ fontSize: 64 }} />
              </div>
              <h2>Cancelamento Confirmado</h2>
              <p>Suas preferências de email foram atualizadas com sucesso.</p>
              <p className="email-info">{maskEmail(email)}</p>
              
              <div className="info-box">
                <Info sx={{ fontSize: 20 }} />
                <span>
                  Emails transacionais (verificação de conta, reset de senha, confirmações de pagamento) 
                  continuarão sendo enviados quando necessário.
                </span>
              </div>
              
              <Link to="/" className="btn-back">
                Voltar para PersonalNutri
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }
  
  // Formulário
  return (
    <div className="unsubscribe-page surface-app">
      <div className="unsubscribe-container">
        <Card variant="auth" className="unsubscribe-card">
          <div className="unsubscribe-logo">
            <img src={logoIcon} alt="PersonalNutri" />
          </div>
          
          <div className="unsubscribe-header">
            <Unsubscribe sx={{ fontSize: 32 }} />
            <h1>Cancelar Inscrição de Emails</h1>
          </div>
          
          <p className="email-display">
            <strong>Email:</strong> {maskEmail(email)}
          </p>
          
          <form onSubmit={handleSubmit}>
            <div className="options-section">
              <p className="options-title">Selecione os tipos de email que deseja cancelar:</p>
              
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={options.marketing}
                  onChange={(e) => handleOptionChange('marketing', e.target.checked)}
                  disabled={options.global}
                />
                <div className="checkbox-content">
                  <Campaign sx={{ fontSize: 24 }} className="option-icon marketing" />
                  <div className="option-text">
                    <span className="option-title">Emails de Marketing</span>
                    <span className="option-desc">Promoções, ofertas especiais, novos recursos</span>
                  </div>
                </div>
              </label>
              
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={options.notification}
                  onChange={(e) => handleOptionChange('notification', e.target.checked)}
                  disabled={options.global}
                />
                <div className="checkbox-content">
                  <Notifications sx={{ fontSize: 24 }} className="option-icon notification" />
                  <div className="option-text">
                    <span className="option-title">Emails de Notificação</span>
                    <span className="option-desc">Lembretes de refeição, alertas, resumos</span>
                  </div>
                </div>
              </label>
              
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={options.newsletter}
                  onChange={(e) => handleOptionChange('newsletter', e.target.checked)}
                  disabled={options.global}
                />
                <div className="checkbox-content">
                  <Article sx={{ fontSize: 24 }} className="option-icon newsletter" />
                  <div className="option-text">
                    <span className="option-title">Newsletter</span>
                    <span className="option-desc">Dicas de nutrição, artigos, conteúdo semanal</span>
                  </div>
                </div>
              </label>
              
              <div className="separator"></div>
              
              <label className="checkbox-label global-option">
                <input
                  type="checkbox"
                  checked={options.global}
                  onChange={(e) => handleGlobalChange(e.target.checked)}
                />
                <div className="checkbox-content">
                  <BlockOutlined sx={{ fontSize: 24 }} className="option-icon global" />
                  <div className="option-text">
                    <span className="option-title">Cancelar TODOS os emails</span>
                    <span className="option-desc">Exceto emails transacionais obrigatórios</span>
                  </div>
                </div>
              </label>
            </div>
            
            <div className="reason-section">
              <label>
                <span className="reason-label">Motivo (opcional):</span>
                <TextArea
                  value={options.reason}
                  onChange={(e) => setOptions((prev) => ({ ...prev, reason: e.target.value }))}
                  placeholder="Conte-nos por que está cancelando a inscrição..."
                  maxLength={500}
                  rows={3}
                />
                <span className="char-count">{options.reason.length}/500</span>
              </label>
            </div>
            
            {error && (
              <div className="form-error">
                <ErrorIcon sx={{ fontSize: 18 }} />
                {error}
              </div>
            )}
            
            <Button type="submit" variant="primary" loading={submitting} block>
              Confirmar Cancelamento
            </Button>
          </form>
          
          <div className="info-box">
            <Info sx={{ fontSize: 20 }} />
            <span>
              Emails transacionais (verificação de conta, reset de senha, confirmações de pagamento) 
              continuarão sendo enviados quando necessário.
            </span>
          </div>
          
          <Link to="/" className="link-back">
            Voltar para PersonalNutri
          </Link>
        </Card>
      </div>
    </div>
  );
};

export default UnsubscribePage;
